import { DeliveryAddress } from "@/services/settings/settingsService";
import { useTheme } from "@/context/ThemeContext";
import { MapPin, Edit, Trash2 } from "lucide-react";
import { settingsLabels, getCountryDisplayName } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

interface DeliveryAddressCardProps {
  address: DeliveryAddress;
  onSetDefault: (id: string) => void;
  onDelete?: (id: string) => void;
  onEdit?: (id: string) => void;
  canDelete?: boolean;
}

export const DeliveryAddressCard = ({
  address,
  onSetDefault,
  onDelete,
  onEdit,
  canDelete = true,
}: DeliveryAddressCardProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const labels = settingsLabels(getLanguage());
  const deliveryLabels = (labels.delivery ?? {}) as Record<string, string>;

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg ${isDark ? "bg-black/20 border border-white/10" : "bg-white border border-gray-200"}`}
    >
      <div className="flex items-center gap-3">
        <MapPin
          className={`h-4 w-4 ${isDark ? "text-white/60" : "text-black/60"}`}
        />
        <div>
          <p
            className={`text-sm font-medium ${isDark ? "text-white" : "text-black"}`}
          >
            {address.street}, {address.postalCode} {address.city}
          </p>
          <p
            className={`text-sm ${isDark ? "text-white/60" : "text-black/60"}`}
          >
            {getCountryDisplayName(address.country, getLanguage())}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {address.isDefault && (
          <span className="text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">
            {deliveryLabels.defaultBadge ?? "Default"}
          </span>
        )}
        {!address.isDefault && (
          <button
            className="text-xs px-2 py-1 rounded border border-green-500 text-green-500 hover:bg-green-500/10 transition"
            onClick={() => onSetDefault(address.id)}
          >
            {deliveryLabels.setAsDefault ?? "Set as default"}
          </button>
        )}
        {onEdit && (
          <button
            className="text-xs px-1 rounded text-blue-500"
            onClick={() => onEdit(address.id)}
          >
            <Edit className="h-4 w-4 hover:bg-blue-500/10 transition" />
          </button>
        )}
        {onDelete && canDelete && (
          <button
            className="text-xs px-1 rounded text-red-500"
            onClick={() => onDelete(address.id)}
          >
            <Trash2 className="h-4 w-4 hover:bg-red-500/10 transition" />
          </button>
        )}
        {onDelete && !canDelete && (
          <button
            className="text-xs px-1 rounded text-gray-400 cursor-not-allowed"
            disabled
            title={
              deliveryLabels.cannotDeleteLast ??
              "You must have at least one delivery address"
            }
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
